<template>
  <div class="card overflow-hidden">
    <div class="card-body text-center">
      <h2 class="text-dark white-space-pre">
        <slot
          name="header"
          v-bind="{ runners }"
        >
          <HighlightedText
            class="card badge p-2 text-uppercase"
            :text="$t('raceProgress.header', [runners.length && runners[0].firstname])"
          />
        </slot>
      </h2>

      <span
        v-t="{ path: 'raceProgress.catcherCarSpeed', args: [$formatSpeed(speed)] }"
        class="card badge p-2 text-uppercase text-condensed mb-3 smaller"
      />
    </div>

    <div class="race-progress">
      <div class="position-relative ml-6">
        <div class="race-progress-runners">
          <div
            v-for="runner in runners"
            :key="runner.id"
            :style="runnerStyle(runner)"
            :class="$options.colors[runner.gender]"
            :title="`${runner.fullname} - ${$formatDistance((runner.distance / 1000).toFixed(3).slice(0, -2))}`"
            class="race-progress-runner"
          >
            <Avatar
              :runner="runner"
              class="text-primary"
              sm
              :badge="avatarBadge(runner)"
            />
          </div>
        </div>
        <div
          :style="carStyle"
          class="race-progress-catcher-car"
        >
          <img
            src="@/assets/catcher-car.png"
            class="car-icon"
            alt="Catcher car"
            :title="`${$formatSpeed(speed)} ${$formatDistance(distance)}`"
            width="182"
            height="89"
          >
        </div>
      </div>
      <div class="position-relative bg-light pl-6">
        <div class="progress-area position-relative">
          <div
            class="w-100 progress-background-light position-absolute"
            style="inset: 0"
          />
          <div
            :style="barStyle"
            :aria-valuenow="$distance"
            :aria-valuemin="$options.min"
            :aria-valuemax="$max"
            class="progress-area-bg flex-row justify-content-between position-absolute"
            role="progressbar"
            style="inset:0"
          />
          <div class="progress-bar-labels">
            <span
              v-for="label in labels"
              :key="label"
              class="progress-bar-label"
              v-text="label"
            />
          </div>
        </div>
        <div class="race-progress-marks text-primary ml-6">
          <div
            v-for="runner in runners"
            :key="runner.id"
            :style="runnerStyle(runner)"
            :class="['race-progress-mark', runner.distance < distance + 2 && 'text-white']"
          />

          <div
            v-if="mark"
            :style="markStyle(mark)"
            class="race-progress-mark"
          >
            <icon
              name="goal"
              class="icon-sm"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Avatar from '@/components/Avatar.vue'
import HighlightedText from '@/components/HighlightedText.vue'
import { mapGetters } from 'vuex'
import { icon, colors, status } from '@/constants/participant'
import * as modes from '@/constants/modes'

function m2km(value) {
  return value / 1000
}

export default {
  name: 'RaceProgress',
  min: 0,
  max: 80,
  icon,
  colors,
  status,
  modes,
  components: {
    HighlightedText,
    Avatar
  },
  props: {
    runners: {
      type: Array,
      default: undefined
    },
    mark: {
      type: Number,
      default: undefined
    },
    showNo1Badge: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...mapGetters({
      distance: 'catcherCarDistance',
      speed: 'catcherCarSpeed',
      mode: 'mode'
    }),
    $max() {
      return Math.max(this.$options.max, ...[
        this.$distance,
        ...this.runners.map(({ distance }) => m2km(distance || 0))
      ].map(v => (Math.ceil(v / 10) + 1) * 10))
    },
    $distance() {
      return m2km(this.distance)
    },
    barStyle() {
      return {
        width: `${this.project(this.$distance)}%`
      }
    },
    carStyle() {
      return {
        left: `${this.project(this.$distance)}%`
      }
    },
    labels() {
      const { min } = this.$options

      const labels = []
      for (let i = min; i < this.$max; i += 10) {
        labels.push(i)
      }

      return labels
    }
  },
  methods: {
    /**
     * Project distance to scale and limit to maximum
     * @param value
     * @returns {number}
     */
    project(value) {
      return Math.min(100, (value * 100) / this.$max)
    },
    runnerStyle({ distance = 0 }) {
      return {
        left: `${this.project(m2km(distance))}%`
      }
    },
    markStyle(distance = 0) {
      return {
        left: `${this.project(m2km(distance))}%`
      }
    },
    avatarBadge(runner) {
      const gender = runner?.gender || ''
      const distance = runner?.distance || 0
      if (this.showNo1Badge) {
        return `${gender.toUpperCase()} #1`.trim()
      }
      return this.$formatDistance((distance / 1000).toFixed(3)
        .slice(0, -2))
    }
  }
}
</script>

<style lang="scss">
.progress-area {
  display: flex;
  overflow: hidden;
  line-height: 0;
  font-size: 0.875rem;
  height: $race-progress-height;
  border-bottom-right-radius: subtract($border-radius, $card-border-width);

  &-bg {
    background-color: $secondary;
  }
}
.progress-background-light{
  background-color: #02275452;
}

.race-progress {
  .progress {
    height: $race-progress-height;
    border-bottom-right-radius: subtract($border-radius, $card-border-width);
  }

  .car-icon {
    width: 62px;
    height: 30px;
  }

  &-catcher-car {
    position: absolute;
    bottom: 0;
    left: 0;
    margin-bottom: .125rem;
    transition: $progress-bar-transition;
    transition-property: all;
    transform: translateX(calc(-100% + 1px));
  }

  &-marks {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }

  &-mark {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    transition: $progress-bar-transition;
    transition-property: left;
    color: theme-color('primary');

    .icon {
      vertical-align: middle;
      transform: translateY(.5rem);
    }

    &::before {
      content: '';
      display: block;
      position: absolute;
      top: .21875rem;
      left: 0;
      bottom: .21875rem;
      width: 2px;
      background-color: $primary;
      border-radius: 0.125rem;
      transform: translateX(-1px);
    }
  }

  &-runners {
    position: relative;
    height: 4rem;
  }

  &-runner {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    font-size: $font-size-sm;
    line-height: 1.1;
    transition: $progress-bar-transition;
    transition-property: left;
    transform: translateX(-50%);

    & + & {
      .race-progress-runner-distance {
        left: auto;
        right: 85%;
        text-align: right;
      }
    }
  }

  &-runner-distance {
    position: absolute;
    bottom: 50%;
    left: 85%;
    white-space: nowrap;
  }
}

.progress-bar-labels {
  position: absolute;
  top: 2px;
  left: 0;
  right: 0;
  bottom: 2px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: $font-weight-bold;
  color: $progress-bar-color;
}

.progress-bar-label {
  display: inline-block;
  padding: 3px .25rem 0; // fix font adjacent by adding padding to top
  width: 100%;
  height: 100%;
  line-height: 1;
  transform: translateX(-28%);

  &:first-child {
    border-color: transparent;
    visibility: hidden;
  }
}
</style>
